<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { useExercises } from "~/features/computer/composables/useExercises";
import { chapterHubByUnit } from "~/features/computer/data/chapter-hubs";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { computerMcqChapterByUnit } from "~/features/computer/data/mcq-registry";
import type { ComputerMcqUnit } from "~/features/computer/types/computer";

const props = defineProps<{
  unit: Exclude<ComputerMcqUnit, "python">;
}>();

const exercises = useExercises(props.unit);
const config = chapterHubByUnit[props.unit];
const chapter = computerMcqChapterByUnit[props.unit];
const faqs = [
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke short questions kaise prepare karein?`,
    answer: `Pehle high-priority topics revise karein, phir isi page ke short questions answers ke sath parhein aur chapter hub se MCQs bhi cover karein.`,
  },
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke short questions important hain?`,
    answer: `Haan, ${chapter.shortTitle} chapter ke short questions exam preparation me important hote hain kyun ke concept wording aur quick recall yahin se strong hoti hai.`,
  },
  {
    question: `Chapter ${config.chapterNumber} ${chapter.shortTitle} ke answers kahan milte hain?`,
    answer: `Is page par Chapter ${config.chapterNumber} ${chapter.shortTitle} ke short questions aur un ke simple answers ek jagah milte hain.`,
  },
];
const relatedItems = [
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle}`, path: chapter.routes.chapterTo },
  { name: `Chapter ${config.chapterNumber} ${chapter.shortTitle} MCQs`, path: chapter.routes.mcqTo },
  { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
  { name: "Important Questions", path: "/11th/computer/important-questions/" },
];

usePageSeo({
  title: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Short Questions | Ustaad`,
  description: `Chapter ${config.chapterNumber} ${chapter.shortTitle} short questions aur quick answers Roman Urdu me.`,
  keywords: `chapter ${config.chapterNumber} ${chapter.shortTitle.toLowerCase()} short questions, 11th class computer short questions`,
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: `Ch ${config.chapterNumber} ${chapter.shortTitle}`, item: chapter.routes.chapterTo },
    { name: "Short Questions", item: config.practiceCards[1].to },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: `Chapter ${config.chapterNumber} ${chapter.shortTitle} Short Questions`,
    items: relatedItems,
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">Chapter {{ config.chapterNumber }} {{ chapter.shortTitle }} Short Questions</h1>
        <p class="mt-3 text-sm leading-7 text-slate-200">Important short questions jo exam mein aate hain. High priority chapter points ko quick revision form me yahan parho.</p>
      </section>

      <section class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
        <div v-for="question in exercises.short" :key="question.id" class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
          <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ question.question }}</p>
          <p class="mt-2 text-sm leading-7 text-slate-700 dark:text-slate-300">{{ question.answer }}</p>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white">Related Pages</h2>
        <div class="mt-4 grid gap-3 md:grid-cols-2">
          <NuxtLink to="/11th/computer/pairing-scheme/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Pairing Scheme</NuxtLink>
          <NuxtLink to="/11th/computer/important-questions/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Important Questions</NuxtLink>
          <NuxtLink to="/11th/computer/mcqs/top-100/" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Top 100 MCQs</NuxtLink>
          <NuxtLink :to="chapter.routes.chapterTo" class="rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 dark:border-slate-800 dark:bg-slate-900">Back to Chapter</NuxtLink>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <h2 class="text-lg font-semibold text-slate-900 dark:text-white">FAQs</h2>
        <div class="mt-4 space-y-3">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800"
          >
            <h3 class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-700 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
